class CartNotification extends HTMLElement {
  constructor() {
    super();
    // onBodyClick is kept here: it is bound once and reused symmetrically across
    // open()/close() cycles (body.addEventListener / body.removeEventListener).
    // Its lifetime matches the element's lifetime — no lifecycle migration needed.
    this.onBodyClick = this.handleBodyClick.bind(this);
    // LOW-02 (transitionend stacking): bind once so open() can remove-before-add
    // using the exact same reference on every call.
    this._notifTransitionHandler = this._onOpenTransitionEnd.bind(this);
  }

  connectedCallback() {
    // LOW-02: Resolve DOM references and attach listeners in connectedCallback(),
    // not the constructor. In the constructor, children are not yet appended (HTML
    // parsing appends children AFTER the opening tag fires the constructor but BEFORE
    // the closing tag fires connectedCallback). querySelectorAll in the constructor
    // returned an empty NodeList — close button listeners were silently never added.
    this.notification = this;   // <cart-notification id="cart-notification"> === this
    this.header = document.querySelector('sticky-header');

    this._notifKeyupHandler = (evt) => evt.code === 'Escape' && this.close();
    this.addEventListener('keyup', this._notifKeyupHandler);

    // RD-01: Event delegation on `this` replaces the _closeButtonHandlers[] pattern.
    // renderContents() replaces child section innerHTML (e.g., #cart-notification-product),
    // destroying any close buttons registered via direct binding in connectedCallback().
    // A delegated listener on <cart-notification> itself survives all inner DOM replacement —
    // the host element is never touched by renderContents(). evt.target.closest() correctly
    // traverses nested elements (e.g., SVG icon inside button) up to the matching button.
    this._delegatedCloseHandler = (evt) => {
      if (evt.target.closest('button[type="button"]')) this.close();
    };
    this.addEventListener('click', this._delegatedCloseHandler);
  }

  _onOpenTransitionEnd() {
    // LOW-02: null guard — this.notification is set in connectedCallback(); if
    // somehow open() fires before connection (defensive), skip rather than throw.
    if (!this.notification) return;
    this.notification.focus();
    trapFocus(this.notification);
  }

  _teardownListeners() {
    if (this._notifKeyupHandler) {
      this.removeEventListener('keyup', this._notifKeyupHandler);
      this._notifKeyupHandler = undefined;
    }
    if (this._delegatedCloseHandler) {
      this.removeEventListener('click', this._delegatedCloseHandler);
      this._delegatedCloseHandler = undefined;
    }
  }

  disconnectedCallback() {
    this._teardownListeners();
    // Ensure body click handler is cleaned up if the element disconnects while
    // the notification is open (e.g. Theme Editor section removal mid-display).
    document.body.removeEventListener('click', this.onBodyClick);
  }

  open() {
    if (!this.notification) return;  // RD-08: notification set in connectedCallback(); guard pre-connect edge case
    this.notification.classList.add('animate', 'active');

    // LOW-02: remove before add — same MED-02 class of transitionend stacking bug
    // as was fixed in cart-drawer.js. Under rapid ATC submissions, open() is called
    // once per renderContents() cycle. Without remove-before-add, { once: true }
    // handlers accumulate; only the last fires, prior ones stay bound until the next
    // transition. The named _notifTransitionHandler reference makes removal possible.
    this.notification.removeEventListener('transitionend', this._notifTransitionHandler);
    this.notification.addEventListener('transitionend', this._notifTransitionHandler, { once: true });

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    if (!this.notification) return;  // RD-08: same defensive guard as open()
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      const el = document.getElementById(section.id);
      if (!el) return;
      // Guard against getSectionInnerHTML() returning '' on a selector miss:
      // assigning '' would blank the live section; skip the update to preserve existing content.
      const sectionHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      if (sectionHTML) el.innerHTML = sectionHTML;
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const el = parsed.querySelector(selector);
    return el ? el.innerHTML : '';
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);

// LOW-02: Belt-and-suspenders for Theme Editor section morphing edge cases.
// disconnectedCallback() handles standard section removal; this guard covers
// virtual DOM scenarios where the custom element lifecycle may not fire cleanly.
// Pattern mirrors cinematic-slider.js and the cart-drawer.js guard above.
document.addEventListener('shopify:section:unload', (event) => {
  const cartNotification = event.target.querySelector('cart-notification');
  if (cartNotification) cartNotification._teardownListeners();
});
